import { FiltersData } from "@/models/filter";
import { BackendWidget } from "@/models/widget-v2";
import { convertFiltersToQueryString } from "@/utils/filters-to-query-string";
import logger from "@/utils/logger";
import { useMemo } from "react";
import { useApiMutation, useApiQuery } from "../hooks/use-api";
import { API_CROWLINGO_AUTHED, hasSelectedWorkspace } from "./core";
import { createResourceApi } from "./factory";

// Re-export BackendWidget for backwards compatibility
export type { BackendWidget };

// Create standard CRUD hooks using the factory
const widgetApi = createResourceApi<BackendWidget>({
  resourcePath: "widgets",
  requireWorkspace: true,
});

/**
 * Hook to list all widgets in the workspace
 */
export const useWidgets = widgetApi.useList;

/**
 * Hook to list widgets belonging to a specific dashboard
 * @param dashboardId - The ID of the dashboard to get widgets for
 */
export const useWidgetsByDashboard = (dashboardId?: string) =>
  useApiQuery<BackendWidget[]>(
    { url: `widgets?dashboard.id=${dashboardId}` },
    { enabled: !!dashboardId && hasSelectedWorkspace() },
  );

/**
 * Interface pour l'exécution d'un widget avec filtres
 */
export interface BackendWidgetRun {
  widget: BackendWidget;
  filters?: FiltersData;
}

export const createWidget = (widget: BackendWidget) =>
  API_CROWLINGO_AUTHED.post<BackendWidget>("widgets", widget);

export const deleteWidget = (id: string) =>
  API_CROWLINGO_AUTHED.delete<BackendWidget>(`widgets/${id}`);

export const runWidget = <T>(widget: BackendWidgetRun) =>
  API_CROWLINGO_AUTHED.post<T>("widgets/run", { ...widget, limit: 100 });

// Fonction pour nettoyer le widget en retirant les champs non définis explicitement
const cleanWidget = (widget: BackendWidget): BackendWidget => {
  const cleaned: BackendWidget = {
    chart: widget.chart,
    queries: widget.queries,
  };

  // Toujours inclure les champs de base s'ils sont définis
  if (widget.id !== undefined) cleaned.id = widget.id;
  if (widget.metadata !== undefined) cleaned.metadata = widget.metadata;
  if (widget.doc_type !== undefined) cleaned.doc_type = widget.doc_type;
  if (widget.type !== undefined && widget.type !== null)
    cleaned.type = widget.type;

  // Pour les widgets documents/graph, ne jamais inclure les champs d'agrégation
  if (widget.chart === "documents_widget" || widget.chart === "graph_widget") {
    // Ne garder que les champs essentiels pour le mode SEARCH
    if (widget.size !== undefined) cleaned.size = widget.size;
    if (widget.from_ !== undefined) cleaned.from_ = widget.from_;
    if (widget.sort !== undefined) cleaned.sort = widget.sort;
    if (widget.fields !== undefined) cleaned.fields = widget.fields;
    if (widget.source !== undefined) cleaned.source = widget.source;
    return cleaned;
  }

  // Déterminer le type de widget pour appliquer la bonne structure
  // Aligné sur les doc_type backend (underscore(ClassName))
  const isBucketMetricWidget = [
    "area_widget",
    "bar_widget",
    "line_widget",
    "table_widget",
    "heatmap_widget",
    "treemap_widget",
    "scatter_chart_widget",
    "map_widget",
  ].includes(widget.chart as string);
  const isCategoryMetricWidget = ["pie_widget", "word_cloud_widget"].includes(
    widget.chart as string,
  );
  const isGlobalMetricWidget = [
    "gauge_widget",
    "kpi_widget",
    "kpi_comparison_widget",
  ].includes(widget.chart as string);
  const isTextWidget = widget.chart === "text_widget";

  // Configuration pour BucketMetricWidget (area, bar, line, table, heatmap, treemap, scatter, map)
  if (isBucketMetricWidget) {
    if (widget.bucket_field !== undefined && widget.bucket_field !== null)
      cleaned.bucket_field = widget.bucket_field;
    if (widget.bucket_type !== undefined && widget.bucket_type !== null)
      cleaned.bucket_type = widget.bucket_type;
    if (widget.metrics !== undefined && widget.metrics.length > 0)
      cleaned.metrics = widget.metrics;
    if (widget.size !== undefined) cleaned.size = widget.size;
    if (widget.date_interval !== undefined)
      cleaned.date_interval = widget.date_interval;
    if (widget.numeric_interval !== undefined)
      cleaned.numeric_interval = widget.numeric_interval;
    // Split by for multiple series
    if (widget.split_by !== undefined && widget.split_by !== null)
      cleaned.split_by = widget.split_by;
    if (widget.split_by_size !== undefined)
      cleaned.split_by_size = widget.split_by_size;
  }

  // Configuration pour CategoryMetricChart (pie, word_cloud)
  if (isCategoryMetricWidget) {
    if (widget.field !== undefined && widget.field !== null)
      cleaned.field = widget.field;
    if (widget.metric !== undefined) cleaned.metric = widget.metric;
    if (widget.size !== undefined) cleaned.size = widget.size;
    if (widget.min_doc_count !== undefined)
      cleaned.min_doc_count = widget.min_doc_count;
    if (widget.show_missing !== undefined)
      cleaned.show_missing = widget.show_missing;
    if (widget.missing_label !== undefined)
      cleaned.missing_label = widget.missing_label;
  }

  // Configuration pour GlobalMetricWidget (gauge, kpi)
  if (isGlobalMetricWidget) {
    if (widget.metric !== undefined) cleaned.metric = widget.metric;
    // Pour KPI avec comparaison
    if (
      widget.chart === "kpi_widget" ||
      widget.chart === "kpi_comparison_widget"
    ) {
      if (widget.comparison !== undefined)
        cleaned.comparison = widget.comparison;
      if (widget.date_field !== undefined)
        cleaned.date_field = widget.date_field;
    }
  }

  // Configuration pour TextWidget
  if (isTextWidget) {
    if (widget.blocks !== undefined) cleaned.blocks = widget.blocks;
  }

  return cleaned;
};

export const useRunWidget = <T>(
  widget: BackendWidget,
  filters: FiltersData,
  enabled: boolean = true,
) => {
  // Nettoyer le widget pour ne garder que les champs explicitement définis
  const cleanedWidget = cleanWidget(widget);

  // Pour le widget documents, TOUJOURS exécuter la requête
  const isDocumentsWidget = cleanedWidget.chart === "documents_widget";

  // Vérifier si le widget a des queries valides
  // Pour les widgets sans queries (comme documents), permettre l'exécution
  const hasValidQueries =
    widget.queries && widget.queries.length > 0
      ? !widget.queries.find((e) => e.q.find((q) => !q.field || !q.operator))
      : true; // Si pas de queries, considérer comme valide

  // Pour le widget documents, toujours activer la requête (même si filters est vide)
  // Pour les autres widgets, vérifier que filters existe
  const filtersExist = filters !== null && filters !== undefined;

  // GARANTIR que shouldEnable est true pour documents
  const shouldEnable = isDocumentsWidget
    ? true // TOUJOURS activer pour documents - ABSOLUMENT GARANTI
    : filtersExist && hasValidQueries;

  // Construire la query string Elasticsearch à partir des filtres (inclut trackers_id)
  const elasticsearchQueryString = convertFiltersToQueryString(filters);

  // Construire la configuration de la requête
  const requestConfig = {
    url: widget.id ? `widgets/${widget.id}/run` : "widgets/run",
    method: "POST" as const,
    data: {
      query: elasticsearchQueryString,
      // Pour le widget document, inclure le widget dans le format { self: ... }
      ...(isDocumentsWidget && { self: cleanedWidget }),
    },
  };

  const queryOptions = isDocumentsWidget
    ? {
        enabled: enabled && shouldEnable,
        refetchOnMount: enabled && shouldEnable ? (true as const) : false,
        refetchOnWindowFocus: false,
        refetchOnReconnect: enabled && shouldEnable,
        staleTime: 30_000,
        gcTime: 5 * 60 * 1000,
      }
    : {
        enabled: enabled && shouldEnable,
      };

  return useApiQuery<T | undefined>(requestConfig, queryOptions);
};

export const useRunWidgetPage = <T>(widget: BackendWidgetRun) =>
  useApiQuery<T | undefined>({
    url: "widgets/run",
    method: "POST",
    data: widget,
  });

export const useRunMutationWidget = <T>() =>
  useApiMutation<T | undefined, FiltersData>({
    url: "widgets/run",
    method: "POST",
  });

export const runCancellableWidget =
  <T>(abortSignal: AbortSignal) =>
  (widget: BackendWidgetRun) =>
    API_CROWLINGO_AUTHED.post<T>(
      "widgets/run",
      { ...widget, limit: 100 },
      { signal: abortSignal },
    );

// Fonction pour exécuter un widget avec la nouvelle structure de configuration
// Format attendu: {"self": config_du_widget}
export const runWidgetWithConfig = <T>(widgetConfig: BackendWidget) =>
  API_CROWLINGO_AUTHED.post<T>("widgets/run", { self: widgetConfig });

// Hook pour exécuter un widget avec la nouvelle structure de configuration
export const useRunWidgetWithConfig = <T>(
  widgetConfig: BackendWidget | null,
  enabled: boolean = true,
  filtersData?: FiltersData,
) => {
  // Construire la query string Elasticsearch à partir des filtres (inclut trackers_id)
  const elasticsearchQueryString = useMemo(
    () => (filtersData ? convertFiltersToQueryString(filtersData) : ""),
    [filtersData],
  );

  // Créer une clé de requête stable basée sur le contenu de la configuration
  const configKey = useMemo(() => {
    try {
      const widgetConfigString = JSON.stringify(widgetConfig, (key, value) => {
        if (value && typeof value === "object" && !Array.isArray(value)) {
          return Object.keys(value)
            .sort()
            .reduce(
              (acc, k) => {
                acc[k] = value[k];
                return acc;
              },
              {} as Record<string, unknown>,
            );
        }
        return value;
      });
      return JSON.stringify({
        widgetConfig: widgetConfigString,
        query: elasticsearchQueryString,
      });
    } catch (e) {
      logger.warn("useRunWidgetWithConfig: Failed to serialize configKey", {
        error: e,
      });
      return `${JSON.stringify(widgetConfig)}-${elasticsearchQueryString}`;
    }
  }, [widgetConfig, elasticsearchQueryString]);

  // Construire le body de la requête
  const requestData = useMemo(
    () => ({
      self: widgetConfig,
      ...(filtersData && {
        query: elasticsearchQueryString,
      }),
    }),
    [widgetConfig, elasticsearchQueryString, filtersData, configKey],
  );

  logger.debug("useRunWidgetWithConfig:", {
    widgetConfig: widgetConfig
      ? {
          doc_type: widgetConfig.doc_type,
          chart: widgetConfig.chart,
          bucket_field: widgetConfig.bucket_field,
          metricsCount: widgetConfig.metrics?.length || 0,
        }
      : "null",
    enabled,
    filtersData: filtersData ? "present" : "missing",
    configKey: configKey.substring(0, 100),
    configKeyLength: configKey.length,
  });

  // Construire la configuration axios
  const axiosConfig = useMemo(
    () => ({
      url: "widgets/run",
      method: "POST" as const,
      data: requestData,
      _configKey: configKey,
      _configHash: configKey.substring(0, 16),
    }),
    [requestData, configKey],
  );

  return useApiQuery<T | undefined>(axiosConfig, {
    enabled: enabled && !!widgetConfig,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    staleTime: 30_000,
    gcTime: 5 * 60 * 1000,
  });
};

export const useCreateWidget = widgetApi.useCreate;

/**
 * Hook to get a single widget by ID
 */
export const useWidget = (id?: string) =>
  useApiQuery<BackendWidget>(
    { url: `widgets/${id}` },
    { enabled: !!id && hasSelectedWorkspace() },
  );

/** @deprecated Use useWidget() instead */
export const useGetWidget = useWidget;

/**
 * Hook to update a widget
 */
export const useUpdateWidget = widgetApi.useUpdate;

/**
 * Hook to delete a widget
 */
export const useDeleteWidget = widgetApi.useDelete;
